import { completeUpload, getSessionStatus, initSession, uploadChunk, cancelSession } from '../api/uploadApi';
import { getUploadConfig } from '../api/uploadApi';
import { computeFileKey, deleteSession, getSession, saveSession } from './UploadSessionStore';
import { PersistedSession, ScannedFile, UploadConfig, UploadItem } from './types';

export interface UploadManagerOptions {
  maxConcurrentFiles?: number;
  maxConcurrentChunksPerFile?: number;
  maxRetriesPerChunk?: number;
  onChange: (item: UploadItem) => void;
}

const DEFAULT_MAX_CONCURRENT_FILES = 3;
const DEFAULT_MAX_CONCURRENT_CHUNKS_PER_FILE = 4;
const DEFAULT_MAX_RETRIES_PER_CHUNK = 5;

function backoffDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 15000);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Orchestrates the whole client side of the chunked/resumable upload
 * protocol: queuing, bounded concurrency (both across files and across
 * chunks of one file), pause/resume/cancel, retry-with-backoff, speed/ETA
 * tracking, and resume-after-refresh via UploadSessionStore.
 */
export class UploadManager {
  private readonly items = new Map<string, UploadItem>();
  private readonly fileQueue: string[] = [];
  private readonly abortHandlesByItem = new Map<string, Set<() => void>>();
  private activeFileCount = 0;
  private config: UploadConfig | null = null;
  private batchId: string = crypto.randomUUID();

  constructor(private readonly options: UploadManagerOptions) {}

  async ensureConfig(): Promise<UploadConfig> {
    if (!this.config) {
      this.config = await getUploadConfig();
    }
    return this.config;
  }

  getItems(): UploadItem[] {
    return [...this.items.values()];
  }

  /** Starts a fresh batch — call when the user picks a new drop/selection after a completed one. */
  resetBatch(): void {
    this.batchId = crypto.randomUUID();
  }

  async addFiles(projectName: string, scanned: ScannedFile[]): Promise<void> {
    const config = await this.ensureConfig();

    for (const { file, relativePath } of scanned) {
      if (file.size > config.maxFileSizeBytes) {
        const id = crypto.randomUUID();
        const item: UploadItem = {
          id,
          file,
          relativePath,
          projectName,
          batchId: this.batchId,
          status: 'failed',
          totalChunks: 0,
          chunkSize: config.chunkSizeBytes,
          uploadedChunkIndexes: new Set(),
          bytesUploaded: 0,
          speedBps: 0,
          error: `File exceeds maximum allowed size (${config.maxFileSizeBytes} bytes)`,
        };
        this.items.set(id, item);
        this.emit(item);
        continue;
      }

      const fileKey = computeFileKey(projectName, relativePath, file);
      // eslint-disable-next-line no-await-in-loop
      const persisted = await getSession(fileKey);

      const id = crypto.randomUUID();
      const item: UploadItem = {
        id,
        file,
        relativePath,
        projectName,
        batchId: persisted?.batchId ?? this.batchId,
        status: 'queued',
        uploadId: persisted?.uploadId,
        totalChunks: persisted?.totalChunks ?? Math.max(1, Math.ceil(file.size / config.chunkSizeBytes)),
        chunkSize: persisted?.chunkSize ?? config.chunkSizeBytes,
        uploadedChunkIndexes: new Set(persisted?.uploadedChunkIndexes ?? []),
        bytesUploaded: (persisted?.uploadedChunkIndexes.length ?? 0) * (persisted?.chunkSize ?? config.chunkSizeBytes),
        speedBps: 0,
      };
      this.items.set(id, item);
      this.fileQueue.push(id);
      this.emit(item);
    }

    this.pump();
  }

  pause(id: string): void {
    const item = this.items.get(id);
    if (!item || item.status !== 'uploading') return;
    item.status = 'paused';
    this.abortInFlight(id);
    this.emit(item);
  }

  resume(id: string): void {
    const item = this.items.get(id);
    if (!item || (item.status !== 'paused' && item.status !== 'failed')) return;
    item.status = 'queued';
    item.error = undefined;
    this.emit(item);
    this.fileQueue.push(id);
    this.pump();
  }

  async cancel(id: string): Promise<void> {
    const item = this.items.get(id);
    if (!item) return;
    item.status = 'cancelled';
    this.abortInFlight(id);
    this.emit(item);

    if (item.uploadId) {
      await cancelSession(item.uploadId).catch(() => undefined);
    }
    await deleteSession(computeFileKey(item.projectName, item.relativePath, item.file)).catch(() => undefined);
  }

  retryFailed(): void {
    for (const item of this.items.values()) {
      if (item.status === 'failed') this.resume(item.id);
    }
  }

  /** Restores a previously persisted session as a resumable queued item (used by the "resumable uploads" panel). */
  async addFromPersistedSession(projectName: string, file: File, persisted: PersistedSession): Promise<void> {
    await this.ensureConfig();
    const id = crypto.randomUUID();
    const item: UploadItem = {
      id,
      file,
      relativePath: persisted.relativePath,
      projectName,
      batchId: persisted.batchId,
      status: 'queued',
      uploadId: persisted.uploadId,
      totalChunks: persisted.totalChunks,
      chunkSize: persisted.chunkSize,
      uploadedChunkIndexes: new Set(persisted.uploadedChunkIndexes),
      bytesUploaded: persisted.uploadedChunkIndexes.length * persisted.chunkSize,
      speedBps: 0,
    };
    this.items.set(id, item);
    this.fileQueue.push(id);
    this.emit(item);
    this.pump();
  }

  private pump(): void {
    const maxFiles = this.options.maxConcurrentFiles ?? DEFAULT_MAX_CONCURRENT_FILES;
    while (this.activeFileCount < maxFiles && this.fileQueue.length > 0) {
      const id = this.fileQueue.shift();
      if (!id) continue;
      const item = this.items.get(id);
      if (!item || item.status !== 'queued') continue;

      this.activeFileCount += 1;
      this.uploadFile(item)
        .catch(() => undefined)
        .finally(() => {
          this.activeFileCount -= 1;
          this.pump();
        });
    }
  }

  private async uploadFile(item: UploadItem): Promise<void> {
    item.status = 'uploading';
    this.emit(item);

    try {
      await this.ensureSession(item);
      if ((item.status as string) !== 'uploading') return;

      const pending: number[] = [];
      for (let i = 0; i < item.totalChunks; i += 1) {
        if (!item.uploadedChunkIndexes.has(i)) pending.push(i);
      }

      await this.uploadChunksConcurrently(item, pending);

      if ((item.status as string) === 'paused' || (item.status as string) === 'cancelled') return;

      const result = await completeUpload(item.uploadId!);
      item.result = result;
      item.status = 'completed';
      item.bytesUploaded = item.file.size;
      await deleteSession(computeFileKey(item.projectName, item.relativePath, item.file)).catch(() => undefined);
      this.emit(item);
    } catch (error) {
      if ((item.status as string) === 'paused' || (item.status as string) === 'cancelled') return;
      item.status = 'failed';
      item.error = (error as Error).message;
      this.emit(item);
    }
  }

  private async ensureSession(item: UploadItem): Promise<void> {
    if (item.uploadId) {
      const status = await getSessionStatus(item.uploadId).catch(() => null);
      if (status) {
        item.uploadedChunkIndexes = new Set(status.uploadedChunkIndexes);
        item.totalChunks = status.totalChunks;
        return;
      }
      // Session no longer exists server-side (expired/cleaned up) — start over.
      item.uploadId = undefined;
      item.uploadedChunkIndexes = new Set();
    }

    const totalChunks = Math.max(1, Math.ceil(item.file.size / item.chunkSize));
    const session = await initSession({
      projectName: item.projectName,
      fileName: item.file.name,
      fileSize: item.file.size,
      relativePath: item.relativePath,
      batchId: item.batchId,
      totalChunks,
      chunkSize: item.chunkSize,
      clientMimeType: item.file.type || undefined,
    });

    item.uploadId = session.uploadId;
    item.totalChunks = session.totalChunks;
    item.uploadedChunkIndexes = new Set(session.uploadedChunkIndexes);
    await this.persist(item);
  }

  private async uploadChunksConcurrently(item: UploadItem, pendingIndexes: number[]): Promise<void> {
    if (pendingIndexes.length === 0) return;
    const concurrency = Math.min(
      this.options.maxConcurrentChunksPerFile ?? DEFAULT_MAX_CONCURRENT_CHUNKS_PER_FILE,
      pendingIndexes.length,
    );
    let cursor = 0;

    const worker = async (): Promise<void> => {
      for (;;) {
        if ((item.status as string) === 'paused' || (item.status as string) === 'cancelled') return;
        const myIndex = cursor;
        cursor += 1;
        if (myIndex >= pendingIndexes.length) return;
        await this.uploadChunkWithRetry(item, pendingIndexes[myIndex]);
      }
    };

    await Promise.all(Array.from({ length: concurrency }, worker));
  }

  private async uploadChunkWithRetry(item: UploadItem, chunkIndex: number, attempt = 0): Promise<void> {
    const maxRetries = this.options.maxRetriesPerChunk ?? DEFAULT_MAX_RETRIES_PER_CHUNK;
    const start = chunkIndex * item.chunkSize;
    const end = Math.min(start + item.chunkSize, item.file.size);
    const blob = item.file.slice(start, end);

    let lastLoaded = 0;
    const handle = uploadChunk(item.uploadId!, chunkIndex, blob, (loaded) => {
      item.bytesUploaded += loaded - lastLoaded;
      lastLoaded = loaded;
      this.updateSpeed(item);
      this.emit(item);
    });

    this.trackAbort(item.id, handle.abort);

    try {
      const status = await handle.promise;
      item.uploadedChunkIndexes = new Set(status.uploadedChunkIndexes);
      await this.persist(item);
      this.emit(item);
    } catch (error) {
      if ((item.status as string) === 'paused' || (item.status as string) === 'cancelled') {
        item.bytesUploaded -= lastLoaded;
        return;
      }
      if ((error as Error).name === 'AbortError') return;

      if (attempt < maxRetries) {
        item.bytesUploaded -= lastLoaded;
        await delay(backoffDelayMs(attempt));
        return this.uploadChunkWithRetry(item, chunkIndex, attempt + 1);
      }
      throw error;
    } finally {
      this.untrackAbort(item.id, handle.abort);
    }
  }

  private updateSpeed(item: UploadItem): void {
    const now = performance.now();
    if (item.lastProgressAt !== undefined && item.lastProgressBytes !== undefined) {
      const dtSec = (now - item.lastProgressAt) / 1000;
      if (dtSec > 0.05) {
        const instantBps = (item.bytesUploaded - item.lastProgressBytes) / dtSec;
        // Exponential moving average smooths out per-chunk bursts.
        item.speedBps = item.speedBps === 0 ? instantBps : item.speedBps * 0.7 + instantBps * 0.3;
        item.lastProgressAt = now;
        item.lastProgressBytes = item.bytesUploaded;
      }
    } else {
      item.lastProgressAt = now;
      item.lastProgressBytes = item.bytesUploaded;
    }
  }

  private trackAbort(itemId: string, abort: () => void): void {
    if (!this.abortHandlesByItem.has(itemId)) this.abortHandlesByItem.set(itemId, new Set());
    this.abortHandlesByItem.get(itemId)!.add(abort);
  }

  private untrackAbort(itemId: string, abort: () => void): void {
    this.abortHandlesByItem.get(itemId)?.delete(abort);
  }

  private abortInFlight(itemId: string): void {
    const handles = this.abortHandlesByItem.get(itemId);
    if (!handles) return;
    for (const abort of handles) abort();
    handles.clear();
  }

  private async persist(item: UploadItem): Promise<void> {
    if (!item.uploadId) return;
    const record: PersistedSession = {
      fileKey: computeFileKey(item.projectName, item.relativePath, item.file),
      projectName: item.projectName,
      relativePath: item.relativePath,
      fileSize: item.file.size,
      lastModified: item.file.lastModified,
      uploadId: item.uploadId,
      batchId: item.batchId,
      chunkSize: item.chunkSize,
      totalChunks: item.totalChunks,
      uploadedChunkIndexes: [...item.uploadedChunkIndexes],
      updatedAt: new Date().toISOString(),
    };
    await saveSession(record).catch(() => undefined);
  }

  private emit(item: UploadItem): void {
    this.options.onChange(item);
  }
}
